import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Building2, BadgeCheck } from "lucide-react";

const OFFICE_PHOTOS = [
  "https://images.unsplash.com/photo-1771918521550-13c68e5d2173?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1705909773229-411dab40e09b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.unsplash.com/photo-1578991624414-276ef23a534f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
];

const DETAILS = [
  { icon: MapPin, label: "Registered office", value: "18 Berkeley Mews, Birmingham, West Midlands, B25 8PS, UK" },
  { icon: Building2, label: "Company", value: "World Tour Hub Ltd · Registered in England & Wales · No. 17436422" },
  { icon: Phone, label: "Call our travel experts", value: "+44 20 7946 0000" },
  { icon: Mail, label: "Email support", value: "support@worldtourhub.co.uk" },
  { icon: Clock, label: "Opening hours", value: "Mon–Sun · 08:00 – 22:00 (GMT)" },
];

export const OfficeMap = () => {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-24" data-testid="office-section">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Visit us in Birmingham</span>
        <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-slate-950 mt-2">A real company, a real office</h2>
        <p className="text-slate-500 mt-3">Pop in, call, or email — our travel experts are here in person and online, every day of the week.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-stretch">
        {/* Left: details + office photos */}
        <div className="flex flex-col gap-6">
          <div className="rounded-3xl bg-slate-950 text-white p-7 sm:p-9 grain relative overflow-hidden">
            <div className="relative">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400"><BadgeCheck className="w-4 h-4" /> Verified UK registered business</span>
              <div className="mt-5 space-y-5">
                {DETAILS.map((d) => (
                  <div key={d.label} className="flex items-start gap-3" data-testid={`office-detail-${d.label.split(" ")[0].toLowerCase()}`}>
                    <div className="w-10 h-10 rounded-xl bg-brand/20 flex items-center justify-center shrink-0"><d.icon className="w-5 h-5 text-brand" /></div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-white/50 font-bold">{d.label}</div>
                      <div className="text-sm sm:text-base text-white/90 font-semibold leading-snug">{d.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {OFFICE_PHOTOS.map((src, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="rounded-2xl overflow-hidden h-28 sm:h-36">
                <img src={src} alt={`World Tour Hub office ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right: interactive map */}
        <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-lg min-h-[420px]">
          <iframe
            title="World Tour Hub office location"
            data-testid="office-map"
            src="https://maps.google.com/maps?q=18%20Berkeley%20Mews%20Birmingham%20B25%208PS&t=&z=15&ie=UTF8&iwloc=&output=embed"
            className="w-full h-full min-h-[420px] border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
};
